// ===================================
// RESUME SELECTOR COMPONENT - UPDATED
// src/components/ResumeSelector.js
// Select or upload resume during booking
// FIX: Added delete functionality
// ===================================

import React, { useState, useEffect, useCallback } from 'react';
import FileUpload from './FileUpload';
import resumeApi from '../services/resumeApi';
import './ResumeSelector.css';

const ResumeSelector = ({ selectedResumeId, onSelect }) => {
  const [resumes, setResumes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(null); // Track which resume is being deleted
  const [error, setError] = useState(null);
  const [showUpload, setShowUpload] = useState(false);

  const fetchResumes = useCallback(async () => {
    try {
      setLoading(true);
      const response = await resumeApi.getResumes();
      setResumes(response.resumes || []);
      
      // Auto-select primary resume if none selected
      if (!selectedResumeId && response.resumes?.length > 0) {
        const primary = response.resumes.find(r => r.is_primary);
        if (primary) {
          onSelect(primary);
        }
      }
    } catch (err) {
      setError('Failed to load resumes');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [selectedResumeId, onSelect]);

  useEffect(() => {
    fetchResumes();
  }, [fetchResumes]);

  const handleFileSelect = async (file) => {
    try {
      setUploading(true);
      setError(null);
      
      const response = await resumeApi.uploadResume(file, resumes.length === 0);
      
      // Add new resume to list and select it
      const newResume = response.resume;
      setResumes(prev => [newResume, ...prev]);
      onSelect(newResume);
      setShowUpload(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to upload resume');
    } finally {
      setUploading(false);
    }
  };

  const handleSelect = (resume) => {
    onSelect(resume);
  };

  // =============================================
  // DELETE RESUME HANDLER
  // Best Practice: Soft delete by default
  // =============================================
  const handleDelete = async (e, resumeId) => {
    // Stop propagation so clicking delete doesn't select the resume
    e.stopPropagation();
    
    // Find the resume to show its name
    const resumeToDelete = resumes.find(r => r.id === resumeId);
    const resumeName = resumeToDelete?.extracted_name || resumeToDelete?.original_name || 'this resume';
    
    // Confirm deletion (soft delete - can be restored)
    if (!window.confirm(`Remove "${resumeName}" from your list?\n\nThis will hide the resume but preserve your session history.`)) {
      return;
    }

    try {
      setDeleting(resumeId);
      setError(null);
      
      // BEST PRACTICE: Use soft delete (no hard=true param)
      // This preserves data integrity and session references
      const response = await resumeApi.deleteResume(resumeId, false);
      
      // Remove from local state
      setResumes(prev => prev.filter(r => r.id !== resumeId));
      
      // If the deleted resume was selected, clear selection
      if (selectedResumeId === resumeId) {
        onSelect(null);
      }

      // Show success message if there's a note
      if (response.note) {
        console.log('Resume deleted:', response.note);
      }
      
    } catch (err) {
      const errorData = err.response?.data;
      
      // Handle case where resume is used in sessions
      if (err.response?.status === 409 && errorData?.sessionCount) {
        const forceDelete = window.confirm(
          `This resume is used in ${errorData.sessionCount} interview session(s).\n\n` +
          `• Click "OK" to remove it anyway (session history will show "deleted resume")\n` +
          `• Click "Cancel" to keep the resume\n\n` +
          `Note: Your interview sessions and scores will be preserved.`
        );
        
        if (forceDelete) {
          // Retry with force flag
          try {
            await resumeApi.deleteResume(resumeId, true, true); // hard=true, force=true
            setResumes(prev => prev.filter(r => r.id !== resumeId));
            if (selectedResumeId === resumeId) {
              onSelect(null);
            }
          } catch (forceErr) {
            setError(forceErr.response?.data?.message || 'Failed to delete resume');
          }
        }
      } else {
        setError(errorData?.message || 'Failed to delete resume');
      }
      console.error('Delete resume error:', err);
    } finally {
      setDeleting(null);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="resume-selector loading">
        <div className="spinner"></div>
        <p>Loading your resumes...</p>
      </div>
    );
  }

  return (
    <div className="resume-selector">
      {error && (
        <div className="error-message">
          <span>⚠️</span> {error}
          <button className="error-dismiss" onClick={() => setError(null)}>×</button>
        </div>
      )}

      {resumes.length === 0 && !showUpload ? (
        <div className="no-resumes">
          <div className="no-resumes-icon">📄</div>
          <h3>No Resumes Found</h3>
          <p>Upload your resume to get personalized interview questions</p>
          <button 
            className="btn btn-primary"
            onClick={() => setShowUpload(true)}
          >
            Upload Resume
          </button>
        </div>
      ) : (
        <>
          <div className="resume-list">
            {resumes.map(resume => (
              <div
                key={resume.id}
                className={`resume-option ${selectedResumeId === resume.id ? 'selected' : ''} ${deleting === resume.id ? 'deleting' : ''}`}
                onClick={() => handleSelect(resume)}
              >
                <div className="resume-radio">
                  <div className={`radio-circle ${selectedResumeId === resume.id ? 'checked' : ''}`}>
                    {selectedResumeId === resume.id && <span>✓</span>}
                  </div>
                </div>
                
                <div className="resume-icon">
                  {resume.file_type?.includes('pdf') ? '📕' : '📘'}
                </div>
                
                <div className="resume-details">
                  <div className="resume-name">
                    {resume.extracted_name || resume.original_name}
                    {resume.is_primary && <span className="primary-tag">PRIMARY</span>}
                  </div>
                  <div className="resume-meta">
                    <span>{resume.original_name}</span>
                    <span>•</span>
                    <span>Uploaded {formatDate(resume.created_at)}</span>
                  </div>
                  {resume.career_level && (
                    <div className="resume-level">
                      <span className="level-badge">{resume.career_level}</span>
                      {resume.years_experience && (
                        <span className="experience">{resume.years_experience} years exp.</span>
                      )}
                    </div>
                  )}
                </div>

                {/* =============================================
                    NEW: Delete button
                    ============================================= */}
                <button
                  className="resume-delete-btn"
                  onClick={(e) => handleDelete(e, resume.id)}
                  disabled={deleting === resume.id}
                  title="Delete resume"
                  aria-label="Delete resume"
                >
                  {deleting === resume.id ? (
                    <span className="delete-spinner"></span>
                  ) : (
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      width="18" 
                      height="18" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    >
                      <polyline points="3 6 5 6 21 6"></polyline>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                      <line x1="10" y1="11" x2="10" y2="17"></line>
                      <line x1="14" y1="11" x2="14" y2="17"></line>
                    </svg>
                  )}
                </button>
              </div>
            ))}
          </div>

          <button 
            className="btn btn-secondary upload-new-btn"
            onClick={() => setShowUpload(!showUpload)}
          >
            {showUpload ? 'Cancel' : '+ Upload New Resume'}
          </button>
        </>
      )}

      {showUpload && (
        <div className="upload-section">
          <FileUpload
            onFileSelect={handleFileSelect}
            loading={uploading}
            label="Upload Resume"
            description="PDF, DOC, or DOCX (max 10MB)"
            allowedTypes={['PDF', 'DOC', 'DOCX', 'TXT']}
          />
        </div>
      )}
    </div>
  );
};

export default ResumeSelector;